import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { ClipboardList, AlertCircle } from 'lucide-react';
import { ParsedWorksheet } from './worksheet';
import * as ss from 'simple-statistics';

interface MeasurementSystemsAnalysisProps {
  worksheet: ParsedWorksheet;
  selectedColumnId?: string;
}

const d2Table: Record<number, number> = {
  2: 1.128,
  3: 1.693,
  4: 2.059,
  5: 2.326,
};

const formatNumber = (value: number) => (Number.isFinite(value) ? value.toFixed(4) : '—');

export const MeasurementSystemsAnalysis: React.FC<MeasurementSystemsAnalysisProps> = ({ worksheet, selectedColumnId }) => {
  const [studyType, setStudyType] = useState<'type1' | 'crossed'>('type1');
  const [measurementColumnId, setMeasurementColumnId] = useState(selectedColumnId ?? worksheet.numericColumns[0]?.id ?? '');
  const [referenceValue, setReferenceValue] = useState('10');
  const [partColumnId, setPartColumnId] = useState(worksheet.columns[0]?.id ?? '');
  const [operatorColumnId, setOperatorColumnId] = useState(worksheet.columns[1]?.id ?? worksheet.columns[0]?.id ?? '');

  const measurementColumn = worksheet.numericColumns.find((column) => column.id === measurementColumnId) ?? worksheet.numericColumns[0];
  const partColumn = worksheet.columns.find((column) => column.id === partColumnId) ?? worksheet.columns[0];
  const operatorColumn = worksheet.columns.find((column) => column.id === operatorColumnId) ?? worksheet.columns[1] ?? worksheet.columns[0];

  const type1Study = useMemo(() => {
    if (!measurementColumn || measurementColumn.numericValues.length < 2) return null;
    const reference = Number(referenceValue);
    if (!Number.isFinite(reference)) return null;

    const values = measurementColumn.numericValues;
    const mean = ss.mean(values);
    const sigma = ss.sampleStandardDeviation(values);
    const bias = mean - reference;
    const cg = sigma === 0 ? Infinity : Math.abs(reference) / (6 * sigma);
    const cgk = sigma === 0 ? Infinity : (0.1 * Math.abs(reference)) / (3 * sigma + Math.abs(bias));

    return {
      mean,
      sigma,
      bias,
      cg,
      cgk,
      reference,
      repeatability: sigma,
      min: Math.min(...values),
      max: Math.max(...values),
    };
  }, [measurementColumn, referenceValue]);

  const crossedStudy = useMemo(() => {
    if (!measurementColumn || !partColumn || !operatorColumn) return null;

    const rowCount = Math.min(measurementColumn.values.length, partColumn.values.length, operatorColumn.values.length);
    const rows = Array.from({ length: rowCount }, (_, index) => ({
      part: partColumn.values[index],
      operator: operatorColumn.values[index],
      measurement: Number(measurementColumn.values[index]),
    })).filter((row) => row.part && row.operator && Number.isFinite(row.measurement));

    if (rows.length < 6) return null;

    const cellMap = new Map<string, number[]>();
    rows.forEach((row) => {
      const key = `${row.part}__${row.operator}`;
      const current = cellMap.get(key) ?? [];
      current.push(row.measurement);
      cellMap.set(key, current);
    });

    const replicateCounts = Array.from(cellMap.values()).map((values) => values.length);
    const minReplicates = Math.min(...replicateCounts);
    const d2 = d2Table[Math.min(5, Math.max(2, minReplicates))];
    const cellRanges = Array.from(cellMap.values())
      .filter((values) => values.length >= 2)
      .map((values) => Math.max(...values) - Math.min(...values));

    if (!cellRanges.length) return null;

    const equipmentVariation = ss.mean(cellRanges) / d2;

    const operatorMeans = Array.from(new Set(rows.map((row) => row.operator))).map((operator) => ({
      operator,
      mean: ss.mean(rows.filter((row) => row.operator === operator).map((row) => row.measurement)),
    }));
    const partMeans = Array.from(new Set(rows.map((row) => row.part))).map((part) => ({
      part,
      mean: ss.mean(rows.filter((row) => row.part === part).map((row) => row.measurement)),
    }));

    const reproducibility = operatorMeans.length >= 2 ? ss.sampleStandardDeviation(operatorMeans.map((item) => item.mean)) : 0;
    const partToPart = partMeans.length >= 2 ? ss.sampleStandardDeviation(partMeans.map((item) => item.mean)) : 0;
    const totalVariation = ss.sampleStandardDeviation(rows.map((row) => row.measurement));
    const grr = Math.sqrt(equipmentVariation ** 2 + reproducibility ** 2);
    const grrPercent = totalVariation === 0 ? 0 : (grr / totalVariation) * 100;

    return {
      rows: rows.length,
      operators: operatorMeans,
      parts: partMeans,
      equipmentVariation,
      reproducibility,
      partToPart,
      totalVariation,
      grr,
      grrPercent,
      minReplicates,
    };
  }, [measurementColumn, operatorColumn, partColumn]);

  if (worksheet.columns.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>No worksheet data</AlertTitle>
        <AlertDescription>Paste worksheet data with measurement and grouping columns to run MSA studies.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-amber-600 p-3 text-white">
          <ClipboardList size={24} />
        </div>
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Badge>Ready</Badge>
            <Badge variant="outline">MSA</Badge>
          </div>
          <h2 className="text-2xl font-semibold text-foreground">Measurement Systems Analysis</h2>
          <p className="text-muted-foreground">Run a Type 1 gage check or a crossed study screening workflow from worksheet columns.</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Study setup</CardTitle>
          <CardDescription>Choose the study style and map worksheet columns into measurement-system roles.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="space-y-2">
            <Label>Study type</Label>
            <Select value={studyType} onValueChange={(value: 'type1' | 'crossed') => setStudyType(value)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="type1">Type 1 gage study</SelectItem>
                <SelectItem value="crossed">Crossed study screening</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Measurement column</Label>
            <Select value={measurementColumnId} onValueChange={setMeasurementColumnId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {worksheet.numericColumns.map((column) => (
                  <SelectItem key={column.id} value={column.id}>{column.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {studyType === 'type1' ? (
            <div className="space-y-2 xl:col-span-2">
              <Label>Reference value</Label>
              <Input value={referenceValue} onChange={(event) => setReferenceValue(event.target.value)} />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label>Part column</Label>
                <Select value={partColumnId} onValueChange={setPartColumnId}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {worksheet.columns.map((column) => <SelectItem key={column.id} value={column.id}>{column.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Operator column</Label>
                <Select value={operatorColumnId} onValueChange={setOperatorColumnId}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {worksheet.columns.map((column) => <SelectItem key={column.id} value={column.id}>{column.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Tabs value={studyType} className="space-y-4">
        <TabsList>
          <TabsTrigger value="type1">Type 1 gage study</TabsTrigger>
          <TabsTrigger value="crossed">Crossed study</TabsTrigger>
        </TabsList>

        <TabsContent value="type1" className="space-y-4">
          {!type1Study ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Need a valid reference and repeated readings</AlertTitle>
              <AlertDescription>Select a numeric measurement column with at least two values and enter a numeric reference value.</AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
                <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Reference</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(type1Study.reference)}</div></CardContent></Card>
                <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Observed mean</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(type1Study.mean)}</div></CardContent></Card>
                <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Bias</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(type1Study.bias)}</div></CardContent></Card>
                <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Cg</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(type1Study.cg)}</div></CardContent></Card>
                <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Cgk</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(type1Study.cgk)}</div></CardContent></Card>
              </div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Type 1 study interpretation</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground">
                  <p>Repeatability estimate (σ): {formatNumber(type1Study.repeatability)}</p>
                  <p>Measurement spread: {formatNumber(type1Study.min)} to {formatNumber(type1Study.max)}</p>
                  <p>This view is suited to repeated readings on a reference standard to check short-term bias and repeatability before a broader crossed study.</p>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="crossed" className="space-y-4">
          {!crossedStudy ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Need crossed study structure</AlertTitle>
              <AlertDescription>Select part and operator columns with repeated numeric measurements for each part/operator cell.</AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
                <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Rows used</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{crossedStudy.rows}</div></CardContent></Card>
                <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Min replicates</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{crossedStudy.minReplicates}</div></CardContent></Card>
                <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Equipment variation</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(crossedStudy.equipmentVariation)}</div></CardContent></Card>
                <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Reproducibility</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(crossedStudy.reproducibility)}</div></CardContent></Card>
                <Card><CardHeader className="pb-2"><CardTitle className="text-sm">GRR</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(crossedStudy.grr)}</div></CardContent></Card>
                <Card><CardHeader className="pb-2"><CardTitle className="text-sm">%GRR of total</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(crossedStudy.grrPercent)}%</div></CardContent></Card>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Operator means</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {crossedStudy.operators.map((operator) => (
                      <div key={operator.operator} className="rounded-lg border p-3 text-sm">
                        <div className="font-medium">{operator.operator}</div>
                        <div className="text-muted-foreground">Mean: {formatNumber(operator.mean)}</div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Part means</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 max-h-80 overflow-auto">
                    {crossedStudy.parts.map((part) => (
                      <div key={part.part} className="rounded-lg border p-3 text-sm">
                        <div className="font-medium">{part.part}</div>
                        <div className="text-muted-foreground">Mean: {formatNumber(part.mean)}</div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
